/**
 * HomingMine.ts
 * Pooled proximity-triggered homing mine — Level 2-3 nebula-gauntlet hazard.
 *
 * State machine:
 *   - IDLE   — drifts due-west at `HOMING_MINE_IDLE_DRIFT_PX_S`. Each
 *              tick the mine measures its centre-to-centre distance to
 *              the live ship; if it crosses below
 *              `HOMING_MINE_TRIGGER_RADIUS_PX`, it transitions to
 *              ACTIVE. No homing in this state.
 *   - ACTIVE — scalar-heading homing at constant
 *              `HOMING_MINE_ACTIVE_SPEED_PX_S`, with the heading
 *              rotating toward the ship at up to
 *              `HOMING_MINE_TURN_RATE_RAD_S * dt` per tick. Same
 *              clamped-delta-angle idiom used by `BossMissile`. TTL
 *              ticks down; the mine self-destructs at zero so a mine
 *              that can't catch the ship doesn't orbit forever and
 *              pin the pool.
 *
 * Cull paths (both states):
 *   - Offscreen-LEFT past `HOMING_MINE_OFFSCREEN_MARGIN_PX` — pool
 *     release. An IDLE mine that drifts past the ship counts; an
 *     ACTIVE mine that overshoots equally counts.
 *
 * Pool contract: implements `reset()` for `ObjectPool` recycling.
 *
 * Ship-damage collision is handled in `shipDamage.ts` against the
 * `homing-mine` body kind. Spawner lives in `homingMineSpawner.ts`,
 * rendering in `spaceDrawBuilders.ts`. This file owns kinematics +
 * pool lifecycle only.
 *
 * NO React imports, NO async.
 *
 * @module HomingMine
 */

import { BaseEntity } from './BaseEntity';
import type { LevelRuntime } from '../engine/gameState';
import { spaceState } from '../engine/spaceState';
import Logger from '../utils/logger';
import {
  HOMING_MINE_WIDTH,
  HOMING_MINE_HEIGHT,
  HOMING_MINE_HP,
  HOMING_MINE_IDLE_DRIFT_PX_S,
  HOMING_MINE_ACTIVE_SPEED_PX_S,
  HOMING_MINE_TURN_RATE_RAD_S,
  HOMING_MINE_TRIGGER_RADIUS_PX,
  HOMING_MINE_ACTIVE_TTL_S,
  HOMING_MINE_OFFSCREEN_MARGIN_PX,
} from '../../constants/spaceEnemyConstants';

/** Lifecycle states for a homing mine. */
export type HomingMineState = 'IDLE' | 'ACTIVE';

/**
 * HomingMine — pooled drift-then-home hazard. Spawns IDLE drifting
 * left; activates on proximity to the player ship; homes until TTL
 * expiry or offscreen-left cull.
 *
 * DECISION: Single class with an enum-typed `state` field, not two
 * subclasses. The IDLE→ACTIVE transition is a one-way trigger with
 * shared cull paths; subclassing would split the cull logic across
 * two files for no readability gain.
 */
export class HomingMine extends BaseEntity {
  /** Monotonic ID counter for stable React keys across pool recycling. */
  private static nextId = 1;

  /** Unique spawn identifier — re-issued each `spawn()` call. */
  public spawnId = 0;

  /** Current lifecycle state. */
  public state: HomingMineState = 'IDLE';

  /** Heading in radians. 0 = east, π = west. Only meaningful in ACTIVE. */
  public heading = Math.PI;

  /** Current HP. One ship-projectile hit kills the mine. */
  public hp = 0;

  /** Seconds of TTL remaining in ACTIVE. Unused in IDLE. */
  public ttl = 0;

  constructor() {
    super(0, 0, HOMING_MINE_WIDTH, HOMING_MINE_HEIGHT);
    this.dead = true;
  }

  /**
   * Initialise the mine at a world position. Spawns IDLE drifting
   * due-west at the configured drift speed.
   *
   * @param x - Spawn X in world space.
   * @param y - Spawn Y in world space.
   */
  public spawn(x: number, y: number): void {
    this.spawnId = HomingMine.nextId++;
    this.x = x;
    this.y = y;
    this.state = 'IDLE';
    this.heading = Math.PI;
    this.vx = -HOMING_MINE_IDLE_DRIFT_PX_S;
    this.vy = 0;
    this.hp = HOMING_MINE_HP;
    this.ttl = 0;
    this.dead = false;
  }

  /** Reset state for pool recycling. Called by `ObjectPool.release()`. */
  public reset(): void {
    this.x = 0;
    this.y = 0;
    this.vx = 0;
    this.vy = 0;
    this.state = 'IDLE';
    this.heading = Math.PI;
    this.hp = 0;
    this.ttl = 0;
    this.dead = true;
  }

  /**
   * Advance one fixed timestep.
   *
   * IDLE  → measure distance to ship; if inside trigger radius, flip
   *         to ACTIVE and reset TTL. Otherwise integrate the constant
   *         leftward drift.
   * ACTIVE → rotate heading toward ship (clamped by turn rate),
   *         integrate at constant speed, decrement TTL, self-destruct
   *         on TTL ≤ 0.
   *
   * Both states share the offscreen-LEFT cull at the end.
   *
   * @param dt    - Fixed timestep in seconds.
   * @param level - Unused; kept for `BaseEntity.update` contract.
   */
  public update(dt: number, level: LevelRuntime): void {
    void level;
    if (this.dead) return;

    const ship = spaceState.ship;
    const myCx = this.x + this.width / 2;
    const myCy = this.y + this.height / 2;

    // IDLE proximity check — flips state in place; the rest of this tick
    // then runs under the new state's integrator. DECISION: Same-tick
    // promotion (vs deferring active integration until next frame) keeps
    // the trigger crisp at 60 Hz and matches the BossMissile pattern of
    // applying TTL on the spawn tick.
    if (this.state === 'IDLE' && ship !== null && !ship.dead) {
      const shipCx = ship.x + ship.width / 2;
      const shipCy = ship.y + ship.height / 2;
      const dx = shipCx - myCx;
      const dy = shipCy - myCy;
      // DECISION: Compare squared distances to avoid a sqrt in the
      // hot path. Pure micro-opt — the trigger fires identically.
      const triggerSq = HOMING_MINE_TRIGGER_RADIUS_PX * HOMING_MINE_TRIGGER_RADIUS_PX;
      if (dx * dx + dy * dy <= triggerSq) {
        this.state = 'ACTIVE';
        Logger.debug('HomingMine', 'IDLE → ACTIVE (ship in trigger radius)');
        this.ttl = HOMING_MINE_ACTIVE_TTL_S;
        this.heading = Math.atan2(dy, dx);
      }
    }

    if (this.state === 'IDLE') {
      this.x += this.vx * dt;
      this.y += this.vy * dt;
    } else {
      // ACTIVE — homing turn (only when a ship target is available).
      if (ship !== null && !ship.dead) {
        const shipCx = ship.x + ship.width / 2;
        const shipCy = ship.y + ship.height / 2;
        const desiredHeading = Math.atan2(shipCy - myCy, shipCx - myCx);
        // Normalise the heading delta to (-π, π] so jitter near the ±π
        // wrap doesn't trigger a full 2π turn the wrong way around.
        // Same idiom as BossMissile.
        let delta = desiredHeading - this.heading;
        while (delta > Math.PI) delta -= 2 * Math.PI;
        while (delta < -Math.PI) delta += 2 * Math.PI;
        const maxStep = HOMING_MINE_TURN_RATE_RAD_S * dt;
        if (delta > maxStep) delta = maxStep;
        else if (delta < -maxStep) delta = -maxStep;
        this.heading += delta;
      }
      this.vx = HOMING_MINE_ACTIVE_SPEED_PX_S * Math.cos(this.heading);
      this.vy = HOMING_MINE_ACTIVE_SPEED_PX_S * Math.sin(this.heading);
      this.x += this.vx * dt;
      this.y += this.vy * dt;

      this.ttl -= dt;
      if (this.ttl <= 0) {
        this.dead = true;
        return;
      }
    }

    // Shared offscreen-LEFT cull.
    const leftCullX = spaceState.cameraX - HOMING_MINE_OFFSCREEN_MARGIN_PX;
    if (this.x + this.width < leftCullX) {
      this.dead = true;
    }
  }
}
